import { useCallback } from 'react';
import { useLocalStorage } from 'react-use';
import { useTellerSession } from '~/common/hooks/use-auth/use-teller-session';

const SERIES_STORY_TRACKING_KEY = 'user_series_next_read_story';
const STORY_ID_INDEX_DELIMITER = '*';
const STORY_VIP_DELIMITER = '&';

export type StoredNextStory = {
  storyId: string;
  storySeriesIndex: number;
  isVipOnly: boolean;
};

const stringToMap = (
  str: string | undefined
): Map<string, string> | undefined => {
  if (!str) return;
  try {
    return new Map(JSON.parse(str));
  } catch {
    return;
  }
};

const mapToString = (map: Map<string, string>): string => {
  try {
    return JSON.stringify([...map.entries()]);
  } catch {
    return '';
  }
};

const dataToString = ({
  storyId,
  storySeriesIndex,
  isVipOnly,
}: StoredNextStory): string =>
  `${storyId}${STORY_ID_INDEX_DELIMITER}${storySeriesIndex}${STORY_VIP_DELIMITER}${
    isVipOnly ? '1' : '0'
  }`;

const stringToData = (storedString: string): StoredNextStory | undefined => {
  const storyId = storedString.split(STORY_ID_INDEX_DELIMITER)[0];
  const indexAndVip = storedString.split(STORY_ID_INDEX_DELIMITER)[1];
  const index = indexAndVip?.split(STORY_VIP_DELIMITER)[0];
  const isVip = indexAndVip?.split(STORY_VIP_DELIMITER)[1];
  if (!storyId || !index || !isVip) return;
  return {
    storyId,
    storySeriesIndex: Number(index),
    isVipOnly: isVip === '1',
  };
};
// Store data as this
// userId storage key -> seriesId -> storyId*storySeriesIndex&isVipOnly
//        -> seriesId2 -> storyId2*storySeriesIndex&isVipOnly
// userId2 storage key -> seriesId3 -> storyId3*storySeriesIndex&isVipOnly
// One map per userId, with seriesId as keys and next read story id - index - isVip flag as value
export const useTrackSeriesNextStoryToRead = () => {
  const { session } = useTellerSession();

  const [
    stringifiedStoredSeriesStoryTracking,
    setStringifiedStoredSeriesStoryTracking,
    removeStoredSeriesStoryTracking,
  ] = useLocalStorage<string>(`${SERIES_STORY_TRACKING_KEY}-${session?.uid}`);

  const storeNextStoryToRead = useCallback(
    (
      seriesId: string,
      storyId: string,
      storySeriesIndex: number,
      isVipOnly: boolean
    ) => {
      try {
        if (!session?.uid) return;

        const seriesMap = stringToMap(stringifiedStoredSeriesStoryTracking);
        const stringifiedData = dataToString({
          storyId,
          storySeriesIndex,
          isVipOnly,
        });

        if (!seriesMap) {
          const newSeriesMap = new Map<string, string>([
            [seriesId, stringifiedData],
          ]);
          setStringifiedStoredSeriesStoryTracking(mapToString(newSeriesMap));
          return;
        }
        if (seriesMap) {
          seriesMap.set(seriesId, stringifiedData);
          setStringifiedStoredSeriesStoryTracking(mapToString(seriesMap));
          return;
        }
      } catch {
        return;
      }
    },
    [
      session,
      setStringifiedStoredSeriesStoryTracking,
      stringifiedStoredSeriesStoryTracking,
    ]
  );

  const getLastReadStoryIdAndSeriesIndex = useCallback(
    (seriesId: string): StoredNextStory | undefined => {
      try {
        if (!session?.uid) return;

        const seriesMap = stringToMap(stringifiedStoredSeriesStoryTracking);
        if (!seriesMap) return;
        const storedData = seriesMap.get(seriesId);
        if (!storedData) return;
        return stringToData(storedData);
      } catch {
        return;
      }
    },
    [session?.uid, stringifiedStoredSeriesStoryTracking]
  );

  return {
    storeNextStoryToRead,
    getLastReadStoryIdAndSeriesIndex,
    removeStoredSeriesStoryTracking,
  };
};
